import Box from '@mui/material/Box';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import TaskHistoryDto from '../../dtos/Task/TaskHistoryDto';
import { FormLabel, Grid } from '@mui/material';
import TaskCommentButtonBar from './TaskCommentButtonBar';
import EditCommentModal from '../Modal/EditCommentModal';
import ConfirmDeleteDialog from '../Modal/ConfirmDelete';
import TaskApiService from '../../api/TaskApiService';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useSnackbar } from '../Common/SnackbarProvider';
import AuthUtil from '../Utils/AuthUtil';
import { useSelector } from 'react-redux';

export default function TaskAttachment(props) {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const { showImages, taskChange, setTaskChange, handleDeleted } = props;
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [fullSizeImage, setFullSizeImage] = useState(false);

    const [originalValue, setOriginalValue] = useState('');

    const showAttachment = useMemo(() => showImages && (taskChange.contentType?.includes('image') ?? false), [showImages, taskChange]);
    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isAdmin = useMemo(() => AuthUtil.isAdmin(userToken), [userToken]);

    const taskApiService = new TaskApiService();

    function handleEditComment() {
        setShowEditDialog(true)
    }

    function handleDeleteComment() {
        setShowDeleteDialog(true);
    }

    function handleEditCancel() {
        setTaskChange(originalValue);
        setShowEditDialog(false);
    }

    function handleCommentChange(value) {
        setTaskChange(value);
    }

    async function handleSaveEdit() {
        const response = await taskApiService.updateTaskAttachment(taskChange.taskChangeId, taskChange);

        if(response && response.success) {
            snackbar.success(t('taskAttachment.toast.updated'));
            setShowEditDialog(false);
        } else {
            snackbar.error(t('taskAttachment.toast.updateError'));
        }
    }

    async function handleConfirmDelete() {
        const response = await taskApiService.deleteTaskAttachment(taskChange.taskChangeId);

        if(response && response.success) {
            snackbar.success(t('taskAttachment.toast.deleted'));
        } else {
            snackbar.error(t('taskAttachment.toast.deleteError'));
        }
        setShowDeleteDialog(false);
        handleDeleted();
    }

    function handleFullSizeImageClick() {
        setFullSizeImage(!fullSizeImage);
    }

    useEffect(() => {
        setOriginalValue(`${taskChange.taskChange}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return(
        <Box
            sx={{
                width: '100%',
                border: '1px solid #ddd',
                margin: '10px',
                backgroundColor: 'white',
                padding: '5px',
                display: 'block',
            }}
        >
            <Grid container>
                <Grid item xs={10}>
                    <FormLabel
                        style={{ fontWeight: 'bold' }}
                    >
                        {t('taskHistory.postedBy', { type: taskChange?.taskChangeType ?? '', user: taskChange?.taskChangeUser ?? '', time: taskChange?.taskChangeTime ?? '' })}
                    </FormLabel>
                </Grid>
                {isAdmin &&
                    <Grid item xs={2}>
                        <TaskCommentButtonBar handleEditComment={handleEditComment} handleDeleteComment={handleDeleteComment} style={{marginRight: '2px', marginLeft: 'auto'}} />
                    </Grid>
                }
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={ 12 }>
                                <a href={ taskChange.url } target={ '_blank' } rel="noreferrer">{ taskChange.taskChange }</a>
                            </Grid>
                            {
                                showAttachment && (taskChange.url.includes('.jpg') || taskChange.url.includes('.jpeg') || taskChange.url.includes('.png') || taskChange.url.includes('.gif')) &&
                                    (<div>
                                        <Grid item xs={1}>
                                            {fullSizeImage ? <RemoveIcon onClick={handleFullSizeImageClick} /> : <AddIcon onClick={handleFullSizeImageClick} />}
                                        </Grid>
                                        <Grid item xs={ 11 }>
                                            <img src={taskChange.url} style={{maxWidth: fullSizeImage ? '100%' : '20%'}} alt={''}/>
                                        </Grid>
                                    </div>)
                            }
                        </Grid>
                    </Grid>
            </Grid>
            <EditCommentModal
                open={showEditDialog}
                cancel={handleEditCancel}
                comment={taskChange.taskChange}
                setComment={handleCommentChange}
                save={handleSaveEdit}
                originalValue={originalValue}
                useEditor={false}
            />
            <ConfirmDeleteDialog
                onClose={handleConfirmDelete}
                open={showDeleteDialog}
                title={t('taskAttachment.confirmDeleteTitle')}
                content={t('taskAttachment.confirmDeleteContent')}
            />
        </Box>
    )
}

TaskAttachment.propTypes = {
    showImages: PropTypes.bool.isRequired,
    taskChange: PropTypes.objectOf(TaskHistoryDto),
    setTaskChange: PropTypes.func.isRequired,
}
